"use client";

import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronsUpDownIcon, XIcon } from "lucide-react";
import * as React from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Z_INDEX_CLASS_NAMES } from "@/configs/z-index";
import { cn } from "@/utils/class-name";

export const ComboboxContext: React.Context<{
    chipsRef: React.RefObject<Element | null> | null;
    multiple: boolean;
}> = React.createContext<{
    chipsRef: React.RefObject<Element | null> | null;
    multiple: boolean;
}>({
    chipsRef: null,
    multiple: false,
});

export function Combobox<Value, Multiple extends boolean | undefined = false>(
    props: ComboboxPrimitive.Root.Props<Value, Multiple>,
): React.ReactElement {
    const chipsRef = React.useRef<Element | null>(null);
    return (
        <ComboboxContext.Provider
            value={React.useMemo(() => ({ chipsRef, multiple: !!props.multiple }), [props.multiple])}
        >
            <ComboboxPrimitive.Root {...props} />
        </ComboboxContext.Provider>
    );
}

export function ComboboxChipsInput({
    className,
    size,
    ...props
}: Omit<ComboboxPrimitive.Input.Props, "size"> & {
    size?: "sm" | "default" | "lg" | number;
    ref?: React.Ref<HTMLInputElement>;
}): React.ReactElement {
    const sizeValue = (size ?? "default") as "sm" | "default" | "lg" | number;

    return (
        <ComboboxPrimitive.Input
            className={cn(
                "min-w-12 flex-1 text-base outline-none sm:text-sm [[data-slot=combobox-chip]+&]:ps-0.5",
                sizeValue === "sm" ? "ps-1.5" : "ps-2",
                className,
            )}
            data-size={typeof sizeValue === "string" ? sizeValue : undefined}
            data-slot={"combobox-chips-input"}
            size={typeof sizeValue === "number" ? sizeValue : undefined}
            {...props}
        />
    );
}

type ComboboxInputProps = Omit<ComboboxPrimitive.Input.Props, "size"> & {
    showTrigger?: boolean;
    showClear?: boolean;
    startAddon?: React.ReactNode;
    size?: "sm" | "default" | "lg" | number;
    ref?: React.Ref<HTMLInputElement>;
    triggerProps?: ComboboxPrimitive.Trigger.Props;
    clearProps?: ComboboxPrimitive.Clear.Props;
};

export const ComboboxInput = React.forwardRef<HTMLInputElement, ComboboxInputProps>(
    (
        { className, showTrigger = true, showClear = false, startAddon, size, triggerProps, clearProps, ...props },
        ref,
    ) => {
        const sizeValue = (size ?? "default") as "sm" | "default" | "lg" | number;

        return (
            <ComboboxPrimitive.InputGroup
                className={"relative not-has-[>*.w-full]:w-fit w-full text-foreground has-disabled:opacity-64"}
                data-slot={"combobox-input-group"}
            >
                {startAddon && (
                    <div
                        aria-hidden={"true"}
                        className={
                            "pointer-events-none absolute inset-y-0 start-px z-10 flex items-center ps-[calc(--spacing(3)-1px)] opacity-80 has-[+[data-size=sm]]:ps-[calc(--spacing(2.5)-1px)] [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:-mx-0.5"
                        }
                        data-slot={"combobox-start-addon"}
                    >
                        {startAddon}
                    </div>
                )}
                <ComboboxPrimitive.Input
                    className={cn(
                        startAddon &&
                            "data-[size=sm]:*:data-[slot=combobox-input]:ps-[calc(--spacing(7.5)-1px)] *:data-[slot=combobox-input]:ps-[calc(--spacing(8.5)-1px)] sm:data-[size=sm]:*:data-[slot=combobox-input]:ps-[calc(--spacing(7)-1px)] sm:*:data-[slot=combobox-input]:ps-[calc(--spacing(8)-1px)]",
                        sizeValue === "sm"
                            ? "has-[+[data-slot=combobox-trigger],+[data-slot=combobox-clear]]:*:data-[slot=combobox-input]:pe-6.5"
                            : "has-[+[data-slot=combobox-trigger],+[data-slot=combobox-clear]]:*:data-[slot=combobox-input]:pe-7",
                        className,
                    )}
                    data-slot={"combobox-input"}
                    render={<Input className={"has-disabled:opacity-100"} size={sizeValue} />}
                    ref={ref}
                    {...props}
                />
                {showTrigger && (
                    <ComboboxTrigger
                        className={cn(
                            "absolute top-1/2 inline-flex size-8 shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 outline-none transition-opacity pointer-coarse:after:absolute pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 hover:opacity-100 has-[+[data-slot=combobox-clear]]:hidden sm:size-7 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                            sizeValue === "sm" ? "end-0" : "end-0.5",
                        )}
                        {...triggerProps}
                    >
                        <ComboboxPrimitive.Icon data-slot={"combobox-icon"}>
                            <ChevronsUpDownIcon />
                        </ComboboxPrimitive.Icon>
                    </ComboboxTrigger>
                )}
                {showClear && (
                    <ComboboxClear
                        className={cn(
                            "absolute top-1/2 inline-flex size-8 shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 outline-none transition-opacity pointer-coarse:after:absolute pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 hover:opacity-100 has-[+[data-slot=combobox-clear]]:hidden sm:size-7 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                            sizeValue === "sm" ? "end-0" : "end-0.5",
                        )}
                        {...clearProps}
                    >
                        <XIcon />
                    </ComboboxClear>
                )}
            </ComboboxPrimitive.InputGroup>
        );
    },
);

export function ComboboxTrigger({
    className,
    children,
    ...props
}: ComboboxPrimitive.Trigger.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.Trigger className={className} data-slot={"combobox-trigger"} {...props}>
            {children}
        </ComboboxPrimitive.Trigger>
    );
}

export function ComboboxPopup({
    className,
    children,
    side = "bottom",
    sideOffset = 4,
    alignOffset,
    align = "start",
    anchor: anchorProp,
    ...props
}: ComboboxPrimitive.Popup.Props & {
    align?: ComboboxPrimitive.Positioner.Props["align"];
    sideOffset?: ComboboxPrimitive.Positioner.Props["sideOffset"];
    alignOffset?: ComboboxPrimitive.Positioner.Props["alignOffset"];
    side?: ComboboxPrimitive.Positioner.Props["side"];
    anchor?: ComboboxPrimitive.Positioner.Props["anchor"];
}): React.ReactElement {
    const { chipsRef } = React.useContext(ComboboxContext);
    const anchor = anchorProp ?? chipsRef;

    return (
        <ComboboxPrimitive.Portal>
            <ComboboxPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                anchor={anchor}
                className={cn(Z_INDEX_CLASS_NAMES.POPOVER, "select-none")}
                data-slot={"combobox-positioner"}
                side={side}
                sideOffset={sideOffset}
            >
                <span
                    className={cn(
                        "relative flex max-h-full min-w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-lg/5 transition-[scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                        className,
                    )}
                >
                    <ComboboxPrimitive.Popup
                        className={"flex max-h-[min(var(--available-height),23rem)] flex-1 flex-col text-foreground"}
                        data-slot={"combobox-popup"}
                        {...props}
                    >
                        {children}
                    </ComboboxPrimitive.Popup>
                </span>
            </ComboboxPrimitive.Positioner>
        </ComboboxPrimitive.Portal>
    );
}

const comboboxItemVariants = cva(
    "min-h-8 in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-default items-center rounded-sm py-1 ps-2 pe-4 text-base outline-none data-disabled:pointer-events-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:opacity-64 sm:min-h-7 sm:text-sm [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "grid grid-cols-[1rem_1fr] gap-2",
                none: "",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

export function ComboboxItem({
    className,
    children,
    variant,
    ...props
}: ComboboxPrimitive.Item.Props & VariantProps<typeof comboboxItemVariants>): React.ReactElement {
    return (
        <ComboboxPrimitive.Item
            className={cn(comboboxItemVariants({ variant }), className)}
            data-slot={"combobox-item"}
            {...props}
        >
            {variant === "none" ? (
                children
            ) : (
                <>
                    <ComboboxPrimitive.ItemIndicator className={"col-start-1"}>
                        <svg
                            aria-hidden={"true"}
                            fill={"none"}
                            height={"24"}
                            stroke={"currentColor"}
                            strokeLinecap={"round"}
                            strokeLinejoin={"round"}
                            strokeWidth={"2"}
                            viewBox={"0 0 24 24"}
                            width={"24"}
                            xmlns={"http://www.w3.org/2000/svg"}
                        >
                            <path d={"M5.252 12.7 10.2 18.63 18.748 5.37"} />
                        </svg>
                    </ComboboxPrimitive.ItemIndicator>
                    <div className={"col-start-2"}>{children}</div>
                </>
            )}
        </ComboboxPrimitive.Item>
    );
}

export function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.Separator
            className={cn("mx-2 my-1 h-px bg-border last:hidden", className)}
            data-slot={"combobox-separator"}
            {...props}
        />
    );
}

export function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.Group
            className={cn("[[role=group]+&]:mt-1.5", className)}
            data-slot={"combobox-group"}
            {...props}
        />
    );
}

export function ComboboxGroupLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.GroupLabel
            className={cn("px-2 py-1.5 font-medium text-muted-foreground text-xs", className)}
            data-slot={"combobox-group-label"}
            {...props}
        />
    );
}

export function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.Empty
            className={cn("not-empty:p-2 text-center text-base text-muted-foreground sm:text-sm", className)}
            data-slot={"combobox-empty"}
            {...props}
        />
    );
}

export function ComboboxRow({ className, ...props }: ComboboxPrimitive.Row.Props): React.ReactElement {
    return <ComboboxPrimitive.Row className={className} data-slot={"combobox-row"} {...props} />;
}

export function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props): React.ReactElement {
    return <ComboboxPrimitive.Value data-slot={"combobox-value"} {...props} />;
}

export function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props): React.ReactElement {
    return (
        <ScrollArea scrollbarGutter scrollFade>
            <ComboboxPrimitive.List
                className={cn(
                    "not-empty:scroll-py-1 not-empty:px-1 not-empty:py-1 in-data-has-overflow-y:pe-3",
                    className,
                )}
                data-slot={"combobox-list"}
                {...props}
            />
        </ScrollArea>
    );
}

export function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props): React.ReactElement {
    return <ComboboxPrimitive.Clear className={className} data-slot={"combobox-clear"} {...props} />;
}

export function ComboboxStatus({ className, ...props }: ComboboxPrimitive.Status.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.Status
            className={cn("px-3 py-2 font-medium text-muted-foreground text-xs empty:m-0 empty:p-0", className)}
            data-slot={"combobox-status"}
            {...props}
        />
    );
}

export function ComboboxCollection(props: ComboboxPrimitive.Collection.Props): React.ReactElement {
    return <ComboboxPrimitive.Collection data-slot={"combobox-collection"} {...props} />;
}

export function ComboboxChips({
    className,
    children,
    startAddon,
    ...props
}: ComboboxPrimitive.Chips.Props & {
    startAddon?: React.ReactNode;
}): React.ReactElement {
    const { chipsRef } = React.useContext(ComboboxContext);

    return (
        <ComboboxPrimitive.Chips
            className={cn(
                "relative inline-flex min-h-9 w-full flex-wrap gap-1 rounded-lg border border-input bg-background not-dark:bg-clip-padding p-[calc(--spacing(1)-1px)] text-base shadow-xs/5 outline-none ring-ring/24 transition-shadow *:min-h-7 before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-focus-within:not-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] focus-within:border-ring focus-within:ring-[3px] has-disabled:pointer-events-none has-data-[size=lg]:min-h-10 has-data-[size=sm]:min-h-8 has-aria-invalid:border-destructive/36 has-autofill:bg-foreground/4 has-disabled:opacity-64 has-[:disabled,:focus-within,[aria-invalid]]:shadow-none focus-within:has-aria-invalid:border-destructive/64 focus-within:has-aria-invalid:ring-destructive/16 has-data-[size=lg]:*:min-h-8 has-data-[size=sm]:*:min-h-6 sm:min-h-8 sm:text-sm sm:has-data-[size=lg]:min-h-9 sm:has-data-[size=sm]:min-h-7 sm:*:min-h-6 sm:has-data-[size=lg]:*:min-h-7 sm:has-data-[size=sm]:*:min-h-5 dark:not-has-disabled:bg-input/32 dark:has-autofill:bg-foreground/8 dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-focus-within:not-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                className,
            )}
            data-slot={"combobox-chips"}
            ref={chipsRef as React.Ref<HTMLDivElement> | null}
            {...props}
        >
            {startAddon && (
                <div
                    aria-hidden={"true"}
                    className={
                        "flex shrink-0 items-center ps-2 opacity-80 has-[~[data-size=sm]]:has-[+[data-slot=combobox-chip]]:pe-1.5 has-[~[data-size=sm]]:ps-1.5 has-[+[data-slot=combobox-chip]]:pe-2 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:-ms-0.5 [&_svg]:-me-1.5"
                    }
                    data-slot={"combobox-start-addon"}
                >
                    {startAddon}
                </div>
            )}
            {children}
        </ComboboxPrimitive.Chips>
    );
}

export function ComboboxChip({
    children,
    removeProps,
    ...props
}: ComboboxPrimitive.Chip.Props & {
    removeProps?: ComboboxPrimitive.ChipRemove.Props;
}): React.ReactElement {
    return (
        <ComboboxPrimitive.Chip
            className={
                "flex items-center rounded-[calc(var(--radius-md)-1px)] bg-accent ps-2 font-medium text-accent-foreground text-sm outline-none sm:text-xs/(--text-xs--line-height) [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5"
            }
            data-slot={"combobox-chip"}
            {...props}
        >
            {children}
            <ComboboxChipRemove {...removeProps} />
        </ComboboxPrimitive.Chip>
    );
}

export function ComboboxChipRemove(props: ComboboxPrimitive.ChipRemove.Props): React.ReactElement {
    return (
        <ComboboxPrimitive.ChipRemove
            aria-label={"Remove"}
            className={
                "h-full shrink-0 cursor-pointer px-1.5 opacity-80 hover:opacity-100 [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5"
            }
            data-slot={"combobox-chip-remove"}
            {...props}
        >
            <XIcon />
        </ComboboxPrimitive.ChipRemove>
    );
}

export const useComboboxFilter: typeof ComboboxPrimitive.useFilter = ComboboxPrimitive.useFilter;

export { ComboboxPrimitive };
